import React, {
  createContext, FunctionComponent, useCallback,
} from 'react';
import { Slide, toast, TypeOptions } from 'react-toastify';
import { Box, Flex } from 'rebass/styled-components';
import { ScrollBox } from '../components';
import { ReactComponent as Toastersuccess } from '../assets/toaster_success.svg';
import { ReactComponent as Toastererror } from '../assets/toaster_error.svg';
import { ReactComponent as Toasterinfo } from '../assets/toaster_info.svg';
import { ReactComponent as Toasterwarning } from '../assets/toaster_warning.svg';

export interface NotificationProviderProps {
  toast: any;
  children: React.ReactNode;
  themeMode: 'light' | 'dark';
}

export interface NotificationProviderValues {
  addNotification: (...args: any[]) => void;
}

const typeMap: { [key: string]: TypeOptions } = {
  info: toast.TYPE.INFO,
  error: toast.TYPE.ERROR,
  warn: toast.TYPE.WARNING,
  success: toast.TYPE.SUCCESS,
};

const notificationState = createContext<NotificationProviderValues>({
  addNotification: () => {},
});
const { Provider } = notificationState;

const NotificationProvider: FunctionComponent<NotificationProviderProps> = ({
  toast: injectedToast, themeMode, children,
}: NotificationProviderProps) => {
  const addNotification = useCallback((notification: any, level: string) => {
    // Parse the notification
    // - sometimes the notification is a string,
    // - sometimes it is an error object in the form of notification.message
    // - sometimes it is an error object in the form of notification.response.data.message
    let parsedMessage = notification;
    if (notification && notification.message) {
      parsedMessage = notification.message;
    }
    if (notification
      && notification.response
      && notification.response.data
      && notification.response.data.message) {
      parsedMessage = notification.response.data.message;
    }

    const type = typeMap[level];

    let iconImage;
    switch (type) {
      case 'success':
        iconImage = <Toastersuccess />;
        break;
      case 'error':
        iconImage = <Toastererror />;
        break;
      case 'info':
        iconImage = <Toasterinfo />;
        break;
      case 'warning':
        iconImage = <Toasterwarning />;
        break;
      default:
        iconImage = <Toasterwarning />;
        break;
    }

    injectedToast(
      (
        <Flex>
          <Box className="icon">{iconImage}</Box>
          <ScrollBox>
            <Box className="text" data-testid="toast-message">{parsedMessage}</Box>
          </ScrollBox>
        </Flex>
      ), {
        type,
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: true,
        transition: Slide,
        className: themeMode,
        autoClose: 5000,
        draggable: false,
      },
    );
  }, [injectedToast, themeMode]);

  return (
    <Provider value={{ addNotification }}>
      {children}
    </Provider>
  );
};

export {
  notificationState,
};

export default NotificationProvider;
